import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { patientJourneyApi, type PatientJourneyStep } from "@/integrations/mysql/client";
import { toast } from "@/hooks/use-toast";

export type { PatientJourneyStep };

export function usePatientJourney() {
  const queryClient = useQueryClient();

  const { data: stepsData, isLoading } = useQuery({
    queryKey: ['patient-journey-steps'],
    queryFn:  () => patientJourneyApi.list(),
  });

  const steps = Array.isArray(stepsData) ? stepsData : [];

  const updateStep = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<PatientJourneyStep> }) =>
      patientJourneyApi.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-journey-steps'] });
      toast({ title: "Passo atualizado com sucesso!" });
    },
    onError: (e: Error) =>
      toast({ title: "Erro ao atualizar passo", description: e.message, variant: "destructive" }),
  });

  const createStep = useMutation({
    mutationFn: (data: Omit<PatientJourneyStep, 'id'>) => patientJourneyApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-journey-steps'] });
      toast({ title: "Passo criado com sucesso!" });
    },
    onError: (e: Error) =>
      toast({ title: "Erro ao criar passo", description: e.message, variant: "destructive" }),
  });

  const deleteStep = useMutation({
    mutationFn: (id: string) => patientJourneyApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-journey-steps'] });
      toast({ title: "Passo deletado com sucesso!" });
    },
    onError: (e: Error) =>
      toast({ title: "Erro ao deletar passo", description: e.message, variant: "destructive" }),
  });

  return {
    steps,
    isLoading,
    updateStep: updateStep.mutate,
    createStep: createStep.mutate,
    deleteStep: deleteStep.mutate,
  };
}
